// Food looks (§11.18): one-click starting points, applied to the selection (one undo step per photo plus
// the global undo toast, like any preset).
import { useLang, useT } from '../../i18n';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { useClipboard } from '../../state/clipboard';
import { applyPreset } from '../../features/presets';
import { useState } from 'react';
import {
  applyFilter,
  FOOD_TABS,
  foodTab,
  SIMPLE_FILTERS,
  type FoodTab,
  type SimpleFilter,
} from '../../features/simpleFilters';
import { FOOD_TAB_LABEL } from '../simple/FilterGrid';
import type { Preset } from '../../model/types';

export function applyToSelection(p: Preset) {
  const lib = useLibrary.getState();
  const ids = lib.selected.size ? [...lib.selected] : lib.activeId ? [lib.activeId] : [];
  if (!ids.length) return;
  const before = useEditor.getState().updateMany(ids, (s) => applyPreset(s, p));
  useClipboard
    .getState()
    .setToast({ key: 'presetApplied', vars: { n: ids.length }, undo: { n: ids.length, before } });
}

/** A food filter at full strength on every selected photo (one undo step each + the global undo toast). */
export function applyFilterToSelection(f: SimpleFilter) {
  const lib = useLibrary.getState();
  const ids = lib.selected.size ? [...lib.selected] : lib.activeId ? [lib.activeId] : [];
  if (!ids.length) return;
  const before = useEditor.getState().updateMany(ids, (s) => applyFilter(s, f, 1));
  useClipboard
    .getState()
    .setToast({ key: 'presetApplied', vars: { n: ids.length }, undo: { n: ids.length, before } });
}

const FOOD_FILTERS = SIMPLE_FILTERS.filter((f) => f.group === 'food');

export function FoodPanel() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const [tab, setTab] = useState<FoodTab>('originals');
  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.8 }}>{t('foodHint')}</div>
      <div
        role="tablist"
        style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6, fontSize: 12 }}
      >
        {FOOD_TABS.map((ft) => (
          <button
            key={ft}
            role="tab"
            data-testid={`pro-food-tab-${ft}`}
            aria-selected={tab === ft}
            onClick={() => setTab(ft)}
            style={tab === ft ? { outline: '2px solid var(--accent)' } : undefined}
          >
            {t(FOOD_TAB_LABEL[ft])}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {FOOD_FILTERS.filter((f) => foodTab(f) === tab).map((f) => (
          // Originals keep their M10 test ids (food-food-fresh, …).
          <button
            key={f.id}
            data-testid={f.section ? `food-${f.id}` : `food-food-${f.id}`}
            onClick={() => applyFilterToSelection(f)}
          >
            {lang === 'es' ? f.nameEs : f.name}
          </button>
        ))}
      </div>
    </div>
  );
}
